import AlgoliaTracking from "./algolia-tracking";
import Helpers from "./helpers";

interface TypeaheadOptions {
	charLimit?: number;
	minimumDebounce?: number;
	maximumDebounce?: number;
	termKey?: string;
	cache?: boolean,
	modifierClass?: string;
	requests?: TypeaheadRequest[];
	highlightSearchTerm?: boolean;
	queryReceived?: (query: string) => void;
	handleOwnData?: boolean;
	onItemSelected?: (item: HTMLElement) => void;
	onInputChange?: (term: string) => void;
	resultsItemSelector?: string;
	onSearchResultsShow?: () => void;
	onSearchResultsHide?: () => void;
	renderInHeader?: boolean;
}

interface TypeaheadRequest {
	name: string;
	limit: number;
	source: string;
	resultsReceived?: (results: any) => void;
	resultsKey?: string;
	additionalQueryParameters?: () => string;
	templates: {
		header?: (data) => string;
		footer?: (data) => string;
		notFound?: () => string;
		pending?: () => string;
		suggestion: (results: any) => string;
		error?: () => string;
	}
}

export default class Typeahead {

	private currentElement: HTMLInputElement;
	private containerElement: HTMLDivElement;
	private resultsContainerElement: HTMLDivElement;
	private options: TypeaheadOptions = {
		charLimit: 3,
		minimumDebounce: 350,
		maximumDebounce: 1000,
		termKey: 'term',
		cache: false,
		highlightSearchTerm: true,
		resultsItemSelector: ".tt-suggestion a, .tt-suggestion button"
	};
	private abortControllers: Array<any> = [];
	private currentSearchTerm: string;
	private searchCounts: {
		name: string,
		count: number
	}[] = [];
	private lastKeyPressTime: number = 0;
	private allKeyPressTimes: number[] = [];
	private requestDebounce: number = 0;
	private requestDebouncedFunction: any;
	private hideResultsWhileFocusedTimer: ReturnType<typeof setTimeout>;

	constructor(currentElement: HTMLInputElement, suppliedOptions?: TypeaheadOptions) {

		this.currentElement = currentElement;
		if (suppliedOptions) this.setOptions(suppliedOptions);
		if (!this.options.requests) return;
		this.setup();

	}

	private setOptions(suppliedOptions: TypeaheadOptions) {

		this.options = { ...this.options, ...suppliedOptions };

		const requests = this.options.requests;

		for (let i = 0; i < requests.length; i++) {

			let source = requests[i].source as string;
			let url = this.currentElement.getAttribute(source);
			requests[i].source = url;
			this.searchCounts.push({
				name: requests[i].name,
				count: 0
			});

		}


	}

	private setup() {

		this.attachEvents();

		this.wrapSearchInput();

		this.containerElement = this.currentElement.closest('.br-typeahead');

		this.createContentContainer();

		if (this.options.renderInHeader) {
			this.resultsContainerElement = document.querySelector('.tt-menu');
		} else {
			this.resultsContainerElement = this.containerElement.querySelector('.tt-menu');
		}

	}

	private attachEvents() {

		this.currentElement.addEventListener('input', this.handleInputChange.bind(this));


		this.currentElement.addEventListener('input', () => {

			this.requestDebouncedFunction = Helpers.debounce(() => {
				if (!this.shouldShowResults()) {
					return;
				}
				if (this.options.queryReceived) {
					this.options.queryReceived(this.currentSearchTerm);
				}
				if (!this.options.handleOwnData) {
					this.showResults();
					this.makeRequests(this.currentSearchTerm);
				}
			}, this.requestDebounce);

			this.requestDebouncedFunction();

		});

		this.currentElement.addEventListener('focus', (e: Event) => {
			if (this.currentElement.value.length >= this.options.charLimit) {
				this.showResults();
			}
		});

		document.addEventListener('click', (e) => {
			const targetElement = e.target as HTMLElement;
			if (targetElement && (targetElement.closest('.js-mega-nav-dimmer--search') || (!targetElement.closest('.br-typeahead') && !targetElement.closest('.c-typeahead-wrapper')))) {
				this.hideResults();
			}
		});

		document.addEventListener('keydown', this.handleArrowKeys.bind(this));

	}

	private shouldShowResults() {
		return this.currentSearchTerm.length >= this.options.charLimit;
	}

	private createContentContainer() {
		let content = "";
		for (let i = 0; i < this.options.requests.length; i++) {
			content += `<div class="tt-dataset tt-dataset-${this.options.requests[i].name}"></div>`;
		}
		let styles = "position: absolute; top: 100%; left: 0px; z-index: 100;display: none;";
		let insertTarget = this.containerElement;

		if (this.options.renderInHeader) {
			styles = "position: absolute; top: 100%; z-index: 100;display: none;";

			const jsHeader = document.querySelector('.js-header') as HTMLDivElement;
			if (jsHeader) {
				insertTarget = jsHeader;
			}
			insertTarget.insertAdjacentHTML('beforeend', `
				<div class="c-typeahead-wrapper">
					<div class="js-mega-nav-dimmer js-mega-nav-dimmer--search"><div class="js-mega-nav-dimmer__inner"></div></div>
					<div class="tt-menu" style="${styles}">
						<div class="tt-menu-inner">
							${content}
						</div>
					</div>
				</div>
			`);
		} else {
			insertTarget.insertAdjacentHTML('beforeend', `
				<div class="tt-menu" style="${styles}">
					${content}
				</div>
			`);
		}
	}

	private wrapSearchInput() {
		const wrapper = document.createElement('span');
		let typeAheadClass = 'br-typeahead';
		if (this.options.modifierClass) typeAheadClass += (' ' + this.options.modifierClass);
		wrapper.setAttribute('class', typeAheadClass);
		this.currentElement.parentNode.insertBefore(wrapper, this.currentElement);
		wrapper.appendChild(this.currentElement);
	}

	private handleInputChange() {
		this.abortRequests();
		this.requestDebounce = this.calculateAverageTimeBetweenKeyPresses();
		this.currentSearchTerm = this.currentElement.value;
		if (this.hideResultsWhileFocusedTimer) {
			clearTimeout(this.hideResultsWhileFocusedTimer);
		}
		if (!this.shouldShowResults()) {
			this.hideResultsWhileFocusedTimer = setTimeout(() => {
				this.hideResults();
			}, 400);
		} else {
			this.showResults();
			// show pending templates immediately for instant feedback
			for(let i = 0; i < this.options.requests.length; i++) {
				const currentRequest = this.options.requests[i];
				if (currentRequest.templates.pending) {
					this.renderTemplate(currentRequest, currentRequest.templates.pending(), {
						Query: this.currentElement.value
					});
				}
			}
		}
		if (this.options.onInputChange) {
			this.options.onInputChange(this.currentSearchTerm);
		}
	}

	private calculateAverageTimeBetweenKeyPresses() {
		const currentKeyPressTime = new Date().getTime();
		if (!this.lastKeyPressTime) {
			this.lastKeyPressTime = currentKeyPressTime;
			return this.options.maximumDebounce;
		}
		const timeSinceLastKeyPress = currentKeyPressTime - this.lastKeyPressTime;
		this.allKeyPressTimes.push(timeSinceLastKeyPress);

		// remove any key press times longer than the debounce maximum
		this.allKeyPressTimes = this.allKeyPressTimes.filter((keyPressTime) => {
			return keyPressTime < this.options.maximumDebounce;
		});

		this.lastKeyPressTime = currentKeyPressTime;

		if (!this.allKeyPressTimes.length) {
			return this.options.maximumDebounce;
		}

		// get the average time between key presses
		const sumOfAllKeyPressTimes = this.allKeyPressTimes.reduce((timeA, timeB) => timeA + timeB, 0);
		const averageOfAllKeyPressTimes = sumOfAllKeyPressTimes / this.allKeyPressTimes.length;

		return Math.min(Math.max(averageOfAllKeyPressTimes * 2, this.options.minimumDebounce), this.options.maximumDebounce);
	}

	private showResults() {
		if (!this.shouldShowResults()) {
			return;
		}
		this.resultsContainerElement.style.display = 'block';
		this.resultsContainerElement.classList.add('tt-open');
		if (this.options.onSearchResultsShow) {
			this.options.onSearchResultsShow();
		}
	}

	private hideResults() {
		this.resultsContainerElement.style.display = 'none';
		this.resultsContainerElement.classList.remove('tt-open');
		this.resetSearchCounts();
		this.allKeyPressTimes = [];
		if (this.options.onSearchResultsHide) { 
			this.options.onSearchResultsHide();
		}
	}

	private resetSearchCounts() {
		for (let i = 0; i < this.searchCounts.length; i++) {
			this.searchCounts[i].count = 0;
		}
	}

	private makeRequests(term: string) {

		Helpers.scopedLoop(this.options.requests, (currentRequest: TypeaheadRequest, i: number, context: Typeahead) => {

			let searchCountIndex = this.searchCounts.findIndex((searchCount) => searchCount.name === currentRequest.name);
			this.searchCounts[searchCountIndex].count++;
			const numberOfSearches = this.searchCounts[searchCountIndex].count;

			let url = encodeURI(currentRequest.source + '?desktop=true&triggered-searches=' + numberOfSearches + "&" + context.options.termKey + '=') + encodeURIComponent(term) + encodeURI(currentRequest.additionalQueryParameters ? currentRequest.additionalQueryParameters() : "");
			let abortController = new AbortController();
			let signal = abortController.signal;
			context.abortControllers.push(abortController);

			fetch(context.appendTimeStamp(url), {
				signal: signal
			}).then((response: any) => {

				if (response.ok) {
					return response.json();
				} else {
					throw new Error(response as any);
				}

			}).then((data) => {

				if (currentRequest.resultsReceived) {
					currentRequest.resultsReceived(data);
				}

				AlgoliaTracking.updateAlgoliaFromSearchResponse(data?.Attributes);

				let resultsTemplated = "";
				let resultsArray = currentRequest.resultsKey ? data[currentRequest.resultsKey] : data;
				const trackForAlgolia = currentRequest.source.indexOf('products') > -1;

				if (!resultsArray.length) {
					if (currentRequest.templates.notFound) {
						resultsTemplated = `${currentRequest.templates.notFound()}`;
						context.renderTemplate(currentRequest, resultsTemplated, data);
						context.showResults();
					} else {
						context.hideResults();
					}

				} else {
					if (trackForAlgolia) {
						for (let i = 0; i < resultsArray.length; i++) {
							const currentProduct = resultsArray[i];
							currentProduct.Url = AlgoliaTracking.addAlgoliaQueryToURL(resultsArray[i].Url, resultsArray[i].Guid, 'Autocomplete');
						}
					}
					context.showResults();
					const queryData = {
						Query: term,
						Count: data.TotalResults ? data.TotalResults : resultsArray.length
					}
					for (let i = 0; i < resultsArray.length; i++) {
						resultsTemplated += `${currentRequest.templates.suggestion(resultsArray[i])}`;
					}
					context.renderTemplate(currentRequest, `
					${currentRequest.templates.header ? currentRequest.templates.header(queryData) : ""}
					${resultsTemplated}
					${currentRequest.templates.footer ? currentRequest.templates.footer(queryData) : ""}
				`, data);

					context.highlightSearchTerm(term);
					context.attachSuggestionEvents(trackForAlgolia);

				}

			}).catch((error) => {

				if (error.name !== 'AbortError') {
					if (currentRequest.templates.error) {
						context.renderTemplate(currentRequest, currentRequest.templates.error(), {});
						context.showResults();
					} else {
						this.hideResults();
					}
				}
			
			});

		}, this);

	}

	private highlightSearchTerm(searchTerm: string) {

		if (!this.options.highlightSearchTerm) {
			return;
		}

		const results = this.resultsContainerElement.querySelectorAll('.js-highlightable');
		Typeahead.highlightResults(results, searchTerm);

	}

	private attachSuggestionEvents(trackAlgolia: boolean) {
		const results = this.resultsContainerElement.querySelectorAll(this.options.resultsItemSelector);

		for (let i = 0; i < results.length; i++) {
			results[i].addEventListener('focus', (e) => {
				const targetElement = e.currentTarget as HTMLLinkElement;
				this.currentElement.value = targetElement.textContent.trim();
			});

			results[i].addEventListener('click', (e) => {
				const targetElement = e.currentTarget as HTMLLinkElement;
				this.currentElement.value = targetElement.textContent.trim();
				if (this.options.onItemSelected) {
					this.options.onItemSelected(results[i] as HTMLElement);
				}

				if (trackAlgolia) {
					const productGuid = targetElement.getAttribute('data-product-guid');
					const position = Helpers.getElementIndex(targetElement.closest('.js-autocomplete-search-result-item'));
					AlgoliaTracking.clickedObjectID(productGuid, position, "Autocomplete: Product Clicked");
				}

			});
		}

	}

	private renderTemplate(request: TypeaheadRequest, template: string, data: any) {

		const targetContainer = this.resultsContainerElement.querySelector('.tt-dataset-' + request.name);
		if (targetContainer) {
			targetContainer.innerHTML = template;
		}

	}

	private abortRequests() {

		if (this.requestDebouncedFunction) {
			this.requestDebouncedFunction.cancel();
		}

		if (!this.abortControllers.length) return;
		for (let i = 0; i < this.abortControllers.length; i++) {
			this.abortControllers[i].abort();
		}
		this.abortControllers = [];

	}

	private appendTimeStamp(url) {

		if (!this.options.cache) url += '&_=' + Date.now();
		return url;

	}

	public static highlightResults(suggestions: NodeListOf<Element>, searchTerm: string) {
		const escapedSearchTerm = searchTerm.replace(/[\-\[\]{}()*+?.,\\\^$|#\s]/g, '\\$&');
		const re = new RegExp(`(${escapedSearchTerm})`, "gi");

		for (let i = 0; i < suggestions.length; i++) {
			const resultHTML = suggestions[i].innerHTML;
			const matches = resultHTML.match(re);

			if (matches) {
				const newText = resultHTML.replace(re, (match) => {
					// Ensure we are not replacing inside HTML tags
					return /<[^>]*$/.test(resultHTML.slice(0, resultHTML.indexOf(match))) ? match : `<strong class="tt-highlight">${match}</strong>`;
				});
				suggestions[i].innerHTML = newText;
			}
		}

	}

	private handleArrowKeys(e: KeyboardEvent) {

		const key = Helpers.getKeypressed(e);

		if (!this.resultsContainerElement.classList.contains('tt-open')) {
			return;
		}
		const activeElement = document.activeElement as HTMLElement;

		if (key === 'ArrowDown' || key === 40) {
			const allSuggestions = this.resultsContainerElement.querySelectorAll(this.options.resultsItemSelector);
			const firstLink = this.resultsContainerElement.querySelector(this.options.resultsItemSelector) as HTMLLinkElement;
			const lastLink = allSuggestions[allSuggestions.length - 1] as HTMLLinkElement;

			if (this.currentElement.isEqualNode(activeElement) || lastLink.isEqualNode(activeElement)) {
				firstLink.focus();
			} else if (activeElement.closest('.tt-suggestion')) {
				const nextLink = this.resultsContainerElement.querySelectorAll(this.options.resultsItemSelector)[this.getIndexOfCurrentSuggestion(activeElement) + 1] as HTMLLinkElement;
				nextLink.focus();
			}

			e.preventDefault();
		}

		if (key === 'ArrowUp' || key === 38) {
			const allSuggestions = this.resultsContainerElement.querySelectorAll(this.options.resultsItemSelector);
			const firstLink = this.resultsContainerElement.querySelector(this.options.resultsItemSelector) as HTMLLinkElement;
			const lastLink = allSuggestions[allSuggestions.length - 1] as HTMLLinkElement;

			if (this.currentElement.isEqualNode(activeElement) || firstLink.isEqualNode(activeElement)) {
				lastLink.focus();
			} else if (activeElement.closest('.tt-suggestion')) {
				const nextLink = this.resultsContainerElement.querySelectorAll(this.options.resultsItemSelector)[this.getIndexOfCurrentSuggestion(activeElement) - 1] as HTMLLinkElement;
				nextLink.focus();
			}

			e.preventDefault();
		}

		if (key === 'Escape' || key === 27) {
			this.currentElement.value = "";
			this.abortRequests();
			this.hideResults();
			this.currentElement.focus();
		}

	}

	private getIndexOfCurrentSuggestion(activeLink: HTMLElement) {

		const allSuggestions = this.resultsContainerElement.querySelectorAll(this.options.resultsItemSelector);
		for (let i = 0; i < allSuggestions.length; i++) {
			if (allSuggestions[i].isEqualNode(activeLink)) {
				return i;
			}
		}
		return 0;

	}

}
